import { Trans, useTranslation } from "react-i18next";
import { useState } from "react";
import { TextField, Label, TextArea } from "react-aria-components";
import { Loader2, TriangleAlert } from "lucide-react";
import type { Manual, ManualState } from "@arc/contracts";
import { Dialog, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import type { AuthSession } from "../../auth/keycloak.js";
import { useShowError } from "../../error/ErrorProvider.js";
import { useManualBody, useMarkManual, type ManualMutationError } from "./useManualMutations.js";

export interface MarkManualDialogProps {
  /** The confirmed manual to flag. `state` is the `expectedFrom` for the atomic guard. */
  manual: { id: number; name: string; state: ManualState };
  session: AuthSession | null;
  onClose: () => void;
  /** Fires with the updated (marked) manual so the caller can refresh an open drawer + announce. */
  onMarked: (updated: Manual) => void;
  /** Fires once on a 409 `MANUAL_STATE_STALE` so the caller can refetch the lists + drop this manual's stale drawer —
   *  otherwise a re-open would carry the same losing `expectedFrom`. The dialog itself stays open with the inline notice. */
  onStale?: () => void;
}

/**
 * Mark a confirmed manual as faulty (ADR-018) — a reason-capture dialog. The location (ADR-041) is optional and
 * behind a disclosure: flagging a whole manual is the common act from here and must not pay for a body fetch, so the
 * outline is pulled only once the reviewer opens "Where is the problem?". The reason is stored on the manual AND in the audit row, so it
 * survives a later unmark. A 409 `MANUAL_STATE_STALE` (the manual left `confirmed` since it was opened) is surfaced inline
 * with a Close affordance; other errors route to the shared error dialog. Mirrors {@link DeleteManualDialog}.
 */
/** The "no selection" sentinel — react-aria keys are string|number, so `""` cannot be one. */
const MARK_NONE = "__none__";

export function MarkManualDialog({ manual, session, onClose, onMarked, onStale }: MarkManualDialogProps) {
  const { t } = useTranslation();
  const mark = useMarkManual(session);
  const showError = useShowError();
  const [comment, setComment] = useState("");
  const [locating, setLocating] = useState(false);
  const [sectionId, setSectionId] = useState<number | "">("");
  const [stepId, setStepId] = useState<number | "">("");
  const body = useManualBody(session, manual.id, locating); // lazy — nothing is fetched until the disclosure opens
  const sections = body.data?.sections ?? [];
  const steps = sections.find((s) => s.id === sectionId)?.steps ?? [];

  const errCode = (mark.error as ManualMutationError | undefined)?.code;
  const isStale = mark.isError && errCode === "MANUAL_STATE_STALE";
  // A location that vanished under the reviewer (another editor deleted the section or step) must NOT cost them the
  // report they just typed — up to 1500 characters (diff panel).
  const badLocation = mark.isError && errCode === "MARK_LOCATION_INVALID";
  // `!isStale` too: after a 409 the expectedFrom is stale until the dialog is closed + reopened on fresh data.
  const canSubmit = comment.trim().length > 0 && !mark.isPending && !isStale;

  const close = () => {
    mark.reset();
    onClose();
  };
  const onSubmit = () => {
    if (!canSubmit) return;
    mark.mutate(
      {
        id: manual.id,
        expectedFrom: manual.state,
        comment: comment.trim(),
        ...(locating && sectionId !== "" ? { sectionId } : {}),
        // The `sectionId !== ""` conjunct is belt-and-braces: the Step select is disabled without a section and a
        // section change clears the step, so this state is unreachable from the UI and no test can pin it. It mirrors
        // the server's own 422 on a bare `stepId`, for a future caller that assembles this body differently.
        ...(locating && sectionId !== "" && stepId !== "" ? { stepId } : {}),
      },
      {
        onSuccess: (updated) => {
          onMarked(updated);
          close();
        },
        onError: (err) => {
          const code = (err as ManualMutationError).code;
          if (code === "MARK_LOCATION_INVALID") {
            // Keep the dialog, the text and the outline: clearing the picked location lets them send it as-is.
            setSectionId("");
            setStepId("");
            return;
          }
          if (code === "MANUAL_STATE_STALE") {
            // Keep the dialog open with the inline stale notice, but tell the caller to refresh the lists + drop the
            // stale drawer so a re-open carries a fresh `expectedFrom` (else it re-sends the same losing CAS — panel).
            onStale?.();
          } else {
            showError(err);
            close();
          }
        },
      },
    );
  };

  return (
    <Dialog isOpen isDismissable={!mark.isPending} onOpenChange={(open) => !open && !mark.isPending && close()}>
      <DialogTitle>{t("manualManagement.mark.title")}</DialogTitle>
      <DialogDescription>
        <Trans
          i18nKey="manualManagement.mark.intro"
          components={{ name: <strong className="text-foreground">{manual.name}</strong> }}
        />
      </DialogDescription>

      <TextField value={comment} onChange={setComment} className="flex flex-col gap-1.5" isDisabled={mark.isPending}>
        <Label className="text-sm">{t("manualManagement.mark.reason")}</Label>
        <TextArea
          autoFocus
          placeholder={t("manualManagement.mark.reasonPlaceholder")}
          maxLength={1500}
          className="border-border bg-background focus-visible:ring-ring/50 min-h-20 rounded-md border p-2 text-sm outline-none focus-visible:ring-2"
        />
      </TextField>

      {/* The location is OPTIONAL and lazy (ADR-041 D5): flagging a whole manual is the common act from the list, so
          the outline is fetched only when a reviewer who knows where the fault is opens this. */}
      {!locating ? (
        <Button variant="ghost" size="sm" className="self-start" onPress={() => setLocating(true)} isDisabled={mark.isPending}>
          {t("manualManagement.mark.where")}
        </Button>
      ) : body.isPending ? (
        <p className="text-muted-foreground text-sm">{t("manualManagement.mark.loadingOutline")}</p>
      ) : body.isError ? (
        <p className="text-muted-foreground text-sm">{t("manualManagement.mark.outlineFailed")}</p>
      ) : (
        <div className="flex flex-col gap-3">
          <Select
            className="text-sm"
            placeholder="—"
            selectedKey={sectionId === "" ? null : sectionId}
            isDisabled={mark.isPending}
            onSelectionChange={(key) => {
              setSectionId(key === MARK_NONE || key == null ? "" : Number(key));
              setStepId("");
            }}
          >
            <Label className="font-medium">{t("manualManagement.mark.section")}</Label>
            <SelectTrigger />
            <SelectContent>
              <SelectItem id={MARK_NONE}>—</SelectItem>
              {sections.map((sec) => (
                <SelectItem key={sec.id} id={sec.id}>
                  {sec.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            className="text-sm"
            placeholder="—"
            isDisabled={sectionId === "" || mark.isPending}
            selectedKey={stepId === "" ? null : stepId}
            onSelectionChange={(key) => setStepId(key === MARK_NONE || key == null ? "" : Number(key))}
          >
            <Label className="font-medium">{t("manualManagement.mark.step")}</Label>
            <SelectTrigger />
            <SelectContent>
              <SelectItem id={MARK_NONE}>—</SelectItem>
              {steps.map((st) => (
                <SelectItem key={st.id} id={st.id}>
                  {st.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {badLocation ? (
        <p role="alert" className="text-destructive text-sm">
          {t("manualManagement.reportLocationGone")}
        </p>
      ) : null}

      {isStale ? (
        <div role="alert" className="text-destructive flex flex-col items-start gap-2 text-sm">
          <span className="flex items-center gap-1.5">
            <TriangleAlert aria-hidden="true" className="size-4 shrink-0" />
            {t("manualManagement.mark.stale")}
          </span>
          <Button size="sm" onPress={close}>
            {t("manualManagement.misc.close")}
          </Button>
        </div>
      ) : null}

      <DialogFooter>
        <Button variant="ghost" onPress={close} isDisabled={mark.isPending}>
          {t("other.cancelButton")}
        </Button>
        <Button variant="default" onPress={onSubmit} isDisabled={!canSubmit}>
          {mark.isPending ? (
            <>
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              {t("manualManagement.mark.marking")}
            </>
          ) : (
            t("manualManagement.mark.title")
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
